// ============================================================================
// LOAN MAPPING HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { loanMappingService } from 'api/services/loanMappingService';
import type { LoanMapping, LoanMappingDetail, LoanMappingFilterParams } from 'api/types/loanMapping.types';
import type { ID, PaginationMeta } from 'api/types';
import { useDataChangeListener } from './useDataSync';

const DATA_KEY = 'loan-mappings';

const EMPTY_PAGINATION: PaginationMeta = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Server-side paginated + filtered loan mappings.
 * Unlike `useLoanMappings` (which fetches everything for dropdown use), this hook
 * sends page / pageSize / filter params to the API and returns only the current
 * page plus pagination metadata. Used by the Loan Mapping Report screen so the
 * grid never holds the whole dataset (fixes the bandwidth-related load failures).
 */
export const useLoanMappingsPaged = (params: LoanMappingFilterParams) => {
  const [data, setData] = useState<LoanMapping[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Serialize params so the effect only re-fires when a value actually changes
  // (object identity would otherwise change every render).
  const paramsKey = JSON.stringify(params);

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await loanMappingService.getAll(JSON.parse(paramsKey) as LoanMappingFilterParams);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [paramsKey]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  useDataChangeListener(DATA_KEY, fetchMappings);

  return { data, pagination, loading, error, refetch: fetchMappings };
};

export const useLoanMappings = () => {
  const [data, setData] = useState<LoanMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await loanMappingService.getAll({ pageSize: 50000 });
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  useDataChangeListener(DATA_KEY, fetchMappings);

  return { data, loading, error, refetch: fetchMappings };
};

export const useLoanMappingDetail = () => {
  const [data, setData] = useState<LoanMappingDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDetail = useCallback(async (id: ID): Promise<LoanMappingDetail | null> => {
    setLoading(true);
    setError(null);
    try {
      const response = await loanMappingService.getById(id);
      setData(response.data);
      return response.data;
    } catch (err) {
      setError(err as Error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchDetail };
};
